/**
 * Audit logging and consent — the two governance mechanisms the architecture is
 * built around rather than bolted onto.
 *
 * Both are enforced server-side. Clients can read their own audit trail and their
 * own consent state, but only Cloud Functions can write either, so a compromised
 * or merely buggy client cannot grant itself consent or erase its own tracks.
 */
import { z } from 'zod'
import { AuditActionSchema, ConsentScopeSchema, ConsentStateSchema, PurposeOfUseSchema, type ConsentScope } from './vocab.js'
import { IsoInstantSchema, idSchema } from './zod-util.js'
import type { AuditEventId, ConsentId, OrgId, PatientId, UserId } from './ids.js'

// ---------------------------------------------------------------------------
// Audit
// ---------------------------------------------------------------------------

/**
 * An immutable audit entry.
 *
 * The unusual field is `purposeOfUse`. Under the HIPAA Privacy Rule the same read
 * of the same record can be permitted or prohibited depending on *why* it happened:
 * identifying a potential trial candidate is a "review preparatory to research",
 * which does not authorise contacting them. A log that records only who read what
 * cannot answer the question an auditor will actually ask, so the purpose is
 * captured at the moment of access and is a required field.
 */
export const AuditEventSchema = z.object({
  id: idSchema<AuditEventId>('audit event id'),
  action: AuditActionSchema,
  purposeOfUse: PurposeOfUseSchema,

  actorUserId: idSchema<UserId>('user id'),
  actorRole: z.string().min(1),
  actorOrgId: idSchema<OrgId>('org id').optional(),

  /** What was touched. `resourceType` plus `resourceId` rather than a typed union, so new resources need no migration. */
  resourceType: z.enum(['PATIENT', 'CANDIDATE', 'TRIAL', 'COHORT_QUERY', 'CONSENT', 'USER', 'MODEL', 'DOCUMENT']),
  resourceId: z.string().min(1),
  /** The patient whose PHI was involved, when any was. Lets a patient be shown their own access log. */
  subjectPatientId: idSchema<PatientId>('patient id').optional(),

  /** Whether the attempt succeeded. Denials are as interesting as grants. */
  outcome: z.enum(['SUCCESS', 'DENIED', 'ERROR']).default('SUCCESS'),
  /** Why it was denied, for `DENIED`. */
  denialReason: z.string().optional(),

  /**
   * Break-glass access: an emergency override of the normal permission check.
   * Always allowed, always loudly logged, always reviewed. A system with no
   * break-glass path gets one anyway, informally and untracked.
   */
  breakGlass: z.boolean().default(false),
  breakGlassJustification: z.string().optional(),

  /** Coarse request context. No full IP — a truncated one is enough to spot anomalies. */
  ipPrefix: z.string().optional(),
  userAgent: z.string().optional(),

  at: IsoInstantSchema,
})
export type AuditEvent = z.infer<typeof AuditEventSchema>

/** Actions that touch PHI and therefore must always produce an audit entry. */
export const PHI_ACTIONS: readonly AuditEvent['action'][] = [
  'PATIENT_RECORD_VIEW',
  'PATIENT_SEARCH',
  'MATCH_RUN',
  'MATCH_VIEW',
  'CANDIDATE_STATE_CHANGE',
  'OUTREACH_SEND',
  'EXPORT',
  'BREAK_GLASS',
]

export function requiresAudit(action: AuditEvent['action']): boolean {
  return PHI_ACTIONS.includes(action)
}

/**
 * Purposes that are legitimate for each action.
 *
 * The pairing that matters: `OUTREACH_SEND` accepts only `RESEARCH_RECRUITMENT`.
 * An outreach logged as `RESEARCH_PREPARATORY` is a compliance violation
 * describing itself, so the write path rejects it rather than recording it.
 */
export const VALID_PURPOSES: Record<AuditEvent['action'], readonly AuditEvent['purposeOfUse'][]> = {
  LOGIN: ['OPERATIONS', 'PATIENT_SELF_ACCESS', 'TREATMENT', 'RESEARCH_PREPARATORY'],
  PATIENT_RECORD_VIEW: ['TREATMENT', 'RESEARCH_PREPARATORY', 'PATIENT_SELF_ACCESS', 'OPERATIONS', 'MODEL_EVALUATION'],
  PATIENT_SEARCH: ['TREATMENT', 'RESEARCH_PREPARATORY', 'OPERATIONS'],
  MATCH_RUN: ['TREATMENT', 'RESEARCH_PREPARATORY', 'PATIENT_SELF_ACCESS', 'MODEL_EVALUATION'],
  MATCH_VIEW: ['TREATMENT', 'RESEARCH_PREPARATORY', 'PATIENT_SELF_ACCESS', 'OPERATIONS'],
  CANDIDATE_STATE_CHANGE: ['TREATMENT', 'RESEARCH_PREPARATORY', 'RESEARCH_RECRUITMENT', 'PATIENT_SELF_ACCESS'],
  COHORT_QUERY_RUN: ['RESEARCH_PREPARATORY', 'OPERATIONS'],
  CONSENT_CHANGE: ['PATIENT_SELF_ACCESS', 'RESEARCH_RECRUITMENT', 'OPERATIONS'],
  OUTREACH_SEND: ['RESEARCH_RECRUITMENT'],
  EXPORT: ['RESEARCH_PREPARATORY', 'OPERATIONS', 'MODEL_EVALUATION'],
  ROLE_CHANGE: ['OPERATIONS'],
  BREAK_GLASS: ['TREATMENT', 'OPERATIONS'],
}

export function isPurposeValid(action: AuditEvent['action'], purpose: AuditEvent['purposeOfUse']): boolean {
  return VALID_PURPOSES[action].includes(purpose)
}

// ---------------------------------------------------------------------------
// Consent
// ---------------------------------------------------------------------------

/**
 * One consent decision, for one scope, by one patient.
 *
 * Consent is per-scope and versioned rather than a single boolean. A patient who
 * agreed to appear in de-identified feasibility counts has not agreed to be
 * telephoned, and a system that cannot represent that difference will eventually
 * act on the wrong one. Revocation is recorded, never deleted — the fact that
 * consent once existed is itself part of the record.
 */
export const ConsentRecordSchema = z.object({
  id: idSchema<ConsentId>('consent id'),
  patientId: idSchema<PatientId>('patient id'),
  scope: ConsentScopeSchema,
  state: ConsentStateSchema,

  /** How it was captured. `IMPLIED_CARE_RELATIONSHIP` covers a treating clinician's own panel. */
  method: z.enum(['WEB_CHECKBOX', 'E_SIGNATURE', 'VERBAL_DOCUMENTED', 'PAPER', 'IMPLIED_CARE_RELATIONSHIP', 'IRB_WAIVER']),
  /** Version of the consent text shown, so we can prove what they actually agreed to. */
  documentVersion: z.string().default('v1'),
  /** IRB protocol number, when the basis is a waiver rather than the patient's own act. */
  irbProtocol: z.string().optional(),

  grantedAt: IsoInstantSchema.optional(),
  revokedAt: IsoInstantSchema.optional(),
  /** Expiry. Consent to be contacted should lapse rather than persist indefinitely. */
  expiresAt: IsoInstantSchema.optional(),

  /** Who recorded it — the patient themselves, or staff documenting a verbal consent. */
  recordedByUserId: idSchema<UserId>('user id'),
  updatedAt: IsoInstantSchema,
})
export type ConsentRecord = z.infer<typeof ConsentRecordSchema>

/**
 * Whether a consent is in force right now.
 *
 * Expiry is evaluated against the passed-in `now` rather than read from the clock,
 * so this stays a pure function and the tests can be deterministic.
 */
export function isConsentActive(record: ConsentRecord, now: string): boolean {
  if (record.state !== 'GRANTED') return false
  if (record.revokedAt) return false
  if (record.expiresAt && record.expiresAt <= now) return false
  return true
}

/** The set of scopes currently in force for a patient. */
export function activeScopes(records: readonly ConsentRecord[], now: string): ConsentScope[] {
  return records.filter((r) => isConsentActive(r, now)).map((r) => r.scope)
}

/** Whether every required scope is in force. Returns the missing ones for the error message. */
export function checkConsent(
  records: readonly ConsentRecord[],
  required: readonly ConsentScope[],
  now: string
): { ok: boolean; missing: ConsentScope[] } {
  const active = new Set(activeScopes(records, now))
  const missing = required.filter((scope) => !active.has(scope))
  return { ok: missing.length === 0, missing }
}

/**
 * Patient-facing consent copy.
 *
 * Written at roughly a 6th–8th grade reading level, in the first person, saying
 * what happens and what stops happening if they decline. Each scope names the
 * concrete action it authorises rather than a category of data processing, because
 * "we may share your health information with research partners" is technically
 * accurate and tells a patient nothing about what will actually occur.
 */
export const CONSENT_COPY: Record<ConsentScope, { title: string; body: string; ifDeclined: string }> = {
  PLATFORM_TERMS: {
    title: 'Use this site',
    body: 'I agree to the terms of use for this research tool.',
    ifDeclined: 'You will not be able to create an account.',
  },
  RECORD_ANALYSIS: {
    title: 'Check my records against studies',
    body: 'Rescure may read my health records to see which research studies I might fit. A computer does this first; a person reviews anything that looks like a match.',
    ifDeclined: 'We will not check your records, so we cannot tell you about studies you might fit.',
  },
  DEIDENTIFIED_FEASIBILITY: {
    title: 'Count me, without naming me',
    body: 'Rescure may include me in totals it shows to research organisations — for example, "84 patients at this hospital could fit this study". My name, birth date, and contact details are never included.',
    ifDeclined: 'You will not be counted in those totals. This does not affect your own study matches.',
  },
  CONTACT_BY_CARE_TEAM: {
    title: 'Let my own doctor bring this up',
    body: 'My doctor or nurse may talk to me about studies they think could help.',
    ifDeclined: 'Your care team will not raise studies with you through this tool.',
  },
  CONTACT_BY_STUDY_TEAM: {
    title: 'Let a study team contact me',
    body: 'A research team from a study I may fit can contact me to explain it and answer questions. They must be approved by an ethics board first. I can change my mind at any time.',
    ifDeclined: 'No study team will contact you. You can still see your matches and raise them with your own doctor.',
  },
  RESEARCH_DATA_SHARING: {
    title: 'Share my records with a study team',
    body: 'After I say I am interested in a study, the study team may see the parts of my health record that relate to that study’s requirements.',
    ifDeclined: 'A study team will not see your records. You can still be contacted and share information yourself.',
  },
}

/** Scopes a patient must actively decide before the product does anything for them. */
export const ONBOARDING_CONSENT_SCOPES: readonly ConsentScope[] = [
  'PLATFORM_TERMS',
  'RECORD_ANALYSIS',
  'CONTACT_BY_STUDY_TEAM',
  'DEIDENTIFIED_FEASIBILITY',
]
